import { Box } from '@mui/material'
import { useEffect, useRef, useState } from 'react'

// Container width (px) at/above which the editor sections pair two-per-row. Measured on the
// CONTAINER (via ResizeObserver), not the viewport, so the chamber's narrow split panel — which
// is narrow even on a wide screen — correctly stays single-column and nothing gets cramped.
const TWO_COLUMN_MIN_WIDTH = 1024

// Sections whose content is intrinsically wide (or belongs full-bleed) and must never be squeezed
// into half a row — most importantly the Drug/Rx medicine table.
const FULL_WIDTH_SECTION_IDS = new Set([
  'drug_advice', // Rx / medicine table — always full width
  'joint_homunculus', // interactive body diagram
  'signature',
  'screening-report',
])

/**
 * Lays the prescription editor sections out responsively: two-per-row when the container is wide
 * enough, one-per-row otherwise (mobile, tablet, chamber split panel). Wide sections (Rx, etc.)
 * always span the full row. Dropdown menus portal to <body>, so a half-width column never clips
 * their content.
 */
export default function SectionGridLayout({ sections, renderSection }) {
  const containerRef = useRef(null)
  const [twoColumns, setTwoColumns] = useState(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const observer = new ResizeObserver((entries) => {
      const width = entries?.[0]?.contentRect?.width || 0
      setTwoColumns(width >= TWO_COLUMN_MIN_WIDTH)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <Box
      ref={containerRef}
      sx={{
        mt: '48px',
        display: 'grid',
        gridTemplateColumns: twoColumns ? 'repeat(2, minmax(0, 1fr))' : '1fr',
        columnGap: 3,
        // Vertical rhythm comes from each section Card's own bottom margin, so rowGap stays 0.
        rowGap: 0,
        alignItems: 'start',
      }}
    >
      {(sections || []).map((section) => {
        const id = section?.id
        const fullWidth = FULL_WIDTH_SECTION_IDS.has(id)
        return (
          <Box
            key={id || `section-${section?.defCode}`}
            sx={{ minWidth: 0, gridColumn: fullWidth ? '1 / -1' : 'auto' }}
          >
            {renderSection(id)}
          </Box>
        )
      })}
    </Box>
  )
}
